import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { chicaService, disponibilidadService, citaService, pagoService, convertirChicaDTO, distritosMapping } from '../services/api';
import { tipos } from '../data/chicasData';
import '../PerfilChica.css';

function PerfilChica() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [chica, setChica] = useState(null);
  const [disponibilidades, setDisponibilidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [menuOpen, setMenuOpen] = useState(false);
  const [imagenActual, setImagenActual] = useState(0);
  
  // Estados para la reserva
  const [fechaSeleccionada, setFechaSeleccionada] = useState('');
  const [horaInicio, setHoraInicio] = useState('00:00');
  const [horas, setHoras] = useState(2);
  const [distritoSeleccionado, setDistritoSeleccionado] = useState('');
  const [reservando, setReservando] = useState(false);
  const [mostrarSelectorHora, setMostrarSelectorHora] = useState(false);

  // Estados para después del pago
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [citaConfirmada, setCitaConfirmada] = useState(null);
  const [esperandoPago, setEsperandoPago] = useState(false);
  const [citaIdEsperando, setCitaIdEsperando] = useState(null);

  // ✅ NUEVOS ESTADOS PARA TEMPORIZADOR
  const [tiempoRestante, setTiempoRestante] = useState(300); // 5 minutos en segundos
  const [citaExpirada, setCitaExpirada] = useState(false);

  useEffect(() => {
    cargarDatosChica();
    
    // Verificar si venimos de un pago exitoso
    const paymentId = searchParams.get('payment_id');
    const status = searchParams.get('status');
    const citaId = searchParams.get('citaId');
    
    if (status === 'approved' && citaId) {
      verificarPagoExitoso(citaId);
    }
  }, [id, searchParams]);

  // ✅ LIMPIAR INTERVALOS AL DESMONTAR
  useEffect(() => {
    return () => {
      if (window.temporizadorPago) {
        clearInterval(window.temporizadorPago);
      }
      if (window.intervaloPago) {
        clearInterval(window.intervaloPago);
      }
    };
  }, []);

  // Cerrar selector de hora al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mostrarSelectorHora && !event.target.closest('.simulador-control')) {
        setMostrarSelectorHora(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mostrarSelectorHora]);

  const cargarDatosChica = async () => {
    try {
      setLoading(true);
      setError(null);

      const todasChicas = await chicaService.obtenerTodas();
      const chicaEncontrada = todasChicas.find(c => c.id === parseInt(id));
      
      if (!chicaEncontrada) {
        setError('Chica no encontrada');
        setLoading(false);
        return;
      }

      const chicaConvertida = convertirChicaDTO(chicaEncontrada);
      
      chicaConvertida.imagenes = [
        chicaConvertida.imagen,
        chicaConvertida.imagen,
        chicaConvertida.imagen
      ];

      setChica(chicaConvertida);

      const disponibilidadesData = await disponibilidadService.obtenerPorChica(parseInt(id));
      setDisponibilidades(disponibilidadesData);

      if (chicaConvertida.distritosDisponibles && chicaConvertida.distritosDisponibles.length > 0) {
        setDistritoSeleccionado(chicaConvertida.distritosDisponibles[0]);
      }

    } catch (err) {
      console.error('Error al cargar datos:', err);
      setError('Error al cargar los datos de la chica');
    } finally {
      setLoading(false);
    }
  };

  const verificarPagoExitoso = async (citaId) => {
    try {
      console.log('🔍 Verificando pago para cita:', citaId);
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const estadoPago = await pagoService.consultarEstado(citaId);
      console.log('📊 Estado del pago:', estadoPago);
      
      if (estadoPago.estado === 'APROBADO') {
        const citaData = await pagoService.obtenerCitaConfirmada(citaId);
        console.log('✅ Cita confirmada:', citaData);
        
        setCitaConfirmada(citaData);
        setMostrarConfirmacion(true);
        setEsperandoPago(false);
      } else if (estadoPago.estado === 'PROCESANDO' || estadoPago.estado === 'PENDIENTE') {
        console.log('⏳ Pago en proceso, reintentando...');
        setTimeout(() => verificarPagoExitoso(citaId), 3000);
      } else {
        alert('El pago no fue aprobado. Estado: ' + estadoPago.estado);
        setEsperandoPago(false);
      }
    } catch (error) {
      console.error('❌ Error al verificar pago:', error);
      alert('Error al verificar el pago: ' + error.message);
      setEsperandoPago(false);
    }
  };

  const handleReservar = async () => {
  console.log('🔵 ========== INICIANDO PROCESO DE RESERVA ==========');
  
  const usuarioActual = JSON.parse(localStorage.getItem('usuarioActual') || 'null');
  
  if (!usuarioActual) {
    alert('Debes iniciar sesión para realizar una reserva');
    navigate('/login');
    return;
  }

  if (!fechaSeleccionada) {
    alert('Por favor selecciona una fecha');
    return;
  }

  if (!distritoSeleccionado) {
    alert('Por favor selecciona un distrito');
    return;
  }

  try {
    setReservando(true);
    console.log('📝 Iniciando proceso de reserva...');

    const distritoKey = Object.keys(distritosMapping).find(
      key => distritosMapping[key] === distritoSeleccionado
    );

    const citaData = {
      usuarioId: usuarioActual.id,
      chicaId: chica.id,
      fecha: fechaSeleccionada,
      horaInicio: horaInicio,
      horas: horas,
      distrito: distritoKey || distritoSeleccionado.toUpperCase().replace(/ /g, '_')
    };

    console.log('📤 Creando cita:', citaData);
    const citaCreada = await citaService.crear(citaData);
    console.log('✅ Cita creada:', citaCreada);

    const pagoData = {
      citaId: citaCreada.id,
      email: usuarioActual.email || 'cliente@kanolima.com',
      telefono: usuarioActual.telefono || ''
    };

    console.log('💳 Creando preferencia de pago:', pagoData);
    const preferenciaResponse = await pagoService.crearPreferencia(pagoData);
    console.log('✅ Preferencia creada:', preferenciaResponse);

    const urlPago = preferenciaResponse.initPoint || preferenciaResponse.sandboxInitPoint;
    
    console.log('🔄 Abriendo Mercado Pago en nueva pestaña...');
    const ventanaPago = window.open(urlPago, '_blank', 'width=800,height=600');

    if (!ventanaPago) {
      alert('Por favor habilita las ventanas emergentes para completar el pago');
      setReservando(false);
      return;
    }

    // ✅ INICIAR ESTADO DE ESPERA CON TEMPORIZADOR
    setEsperandoPago(true);
    setCitaIdEsperando(citaCreada.id);
    setTiempoRestante(300); // 5 minutos
    setCitaExpirada(false);
    
    alert('Se ha abierto Mercado Pago en una nueva pestaña.\n\nTienes 5 minutos para completar el pago.');

    // ✅ TEMPORIZADOR DE CUENTA REGRESIVA CON CIERRE AUTOMÁTICO
    let segundosRestantes = 300;
    let advertenciaShown = false;
    let ventanaCerrada = false;
    
    const temporizador = setInterval(() => {
      segundosRestantes--;
      setTiempoRestante(segundosRestantes);
      
      // ✅ Verificar si la ventana fue cerrada manualmente
      if (ventanaPago && ventanaPago.closed && !ventanaCerrada) {
        ventanaCerrada = true;
        console.log('🚪 Usuario cerró la ventana de Mercado Pago');
      }
      
      // Advertencia a los 30 segundos
      if (segundosRestantes === 30 && !advertenciaShown) {
        advertenciaShown = true;
        console.warn('⚠️ Quedan 30 segundos para completar el pago');
      }
      
      if (segundosRestantes <= 0) {
        clearInterval(temporizador);
        setCitaExpirada(true);
        
        // ✅ INTENTO 1: Cerrar con blur + close
        if (ventanaPago && !ventanaPago.closed) {
          try {
            ventanaPago.blur();
            ventanaPago.close();
            console.log('🚪 Intento 1: Ventana cerrada con blur + close');
          } catch (e) {
            console.log('⚠️ No se pudo cerrar ventana (Intento 1):', e.message);
          }
        }
        
        // ✅ INTENTO 2: Forzar con opener null
        setTimeout(() => {
          if (ventanaPago && !ventanaPago.closed) {
            try {
              ventanaPago.opener = null;
              ventanaPago.close();
              console.log('🚪 Intento 2: Ventana cerrada con opener = null');
            } catch (e) {
              console.log('⚠️ No se pudo cerrar ventana (Intento 2):', e.message);
            }
          }
        }, 500);
        
        // ✅ INTENTO 3: Redirigir a about:blank
        setTimeout(() => {
          if (ventanaPago && !ventanaPago.closed) {
            try {
              ventanaPago.location.href = 'about:blank';
              setTimeout(() => {
                ventanaPago.close();
                console.log('🚪 Intento 3: Ventana cerrada después de about:blank');
              }, 100);
            } catch (e) {
              console.log('⚠️ No se pudo cerrar ventana (Intento 3):', e.message);
              console.log('ℹ️ El usuario deberá cerrar la ventana manualmente');
            }
          }
        }, 1000);
        
        console.log('⏰ Tiempo de pago expirado');
      }
    }, 1000);

    window.temporizadorPago = temporizador;

    // ✅ DETECTOR DE CIERRE MANUAL DE VENTANA
    const detectarCierreVentana = setInterval(() => {
      if (ventanaPago && ventanaPago.closed && !ventanaCerrada) {
        ventanaCerrada = true;
        console.log('🚪 Usuario cerró la ventana de Mercado Pago manualmente');
        
        // Preguntar al usuario qué hacer
        const continuar = window.confirm(
          'Cerraste la ventana de pago.\n\n' +
          '¿Deseas seguir esperando el pago?\n\n' +
          'Click en "Aceptar" para continuar esperando\n' +
          'Click en "Cancelar" para cancelar la reserva'
        );
        
        if (!continuar) {
          clearInterval(temporizador);
          clearInterval(detectarCierreVentana);
          clearInterval(intervaloPago);
          setCitaExpirada(true);
          setEsperandoPago(false);
        } else {
          console.log('✅ Usuario eligió continuar esperando');
        }
        
        clearInterval(detectarCierreVentana);
      }
    }, 1000);

    // ✅ POLLING MEJORADO - Detecta cancelación
    const verificarEstadoPago = async () => {
      try {
        console.log('🔍 Verificando estado del pago...');
        const estadoPago = await pagoService.consultarEstado(citaCreada.id);
        console.log('📊 Estado actual:', estadoPago.estado);

        if (estadoPago.estado === 'APROBADO') {
          console.log('✅ Pago APROBADO');
          clearInterval(temporizador);
          clearInterval(intervaloPago);
          clearInterval(detectarCierreVentana);
          
          const citaData = await pagoService.obtenerCitaConfirmada(citaCreada.id);
          setCitaConfirmada(citaData);
          setMostrarConfirmacion(true);
          setReservando(false);
          setEsperandoPago(false);
          
          // Cerrar ventana si sigue abierta
          if (ventanaPago && !ventanaPago.closed) {
            try {
              ventanaPago.close();
            } catch (e) {
              console.log('No se pudo cerrar la ventana:', e);
            }
          }
          
          return true;
        } 
        
        if (estadoPago.estado === 'CANCELADO') {
          console.warn('⚠️ Cita cancelada');
          clearInterval(temporizador);
          clearInterval(intervaloPago);
          clearInterval(detectarCierreVentana);
          setCitaExpirada(true);
          setReservando(false);
          return true;
        }
        
        if (estadoPago.estado === 'RECHAZADO') {
          console.warn('⚠️ Pago rechazado');
          clearInterval(temporizador);
          clearInterval(intervaloPago);
          clearInterval(detectarCierreVentana);
          alert('El pago fue rechazado. Por favor intenta nuevamente.');
          setReservando(false);
          setEsperandoPago(false);
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Error al verificar estado:', error);
        return false;
      }
    };

    const intervaloPago = setInterval(async () => {
      const completado = await verificarEstadoPago();
      if (completado) {
        clearInterval(intervaloPago);
        clearInterval(detectarCierreVentana);
      }
    }, 3000);

    window.intervaloPago = intervaloPago;

    console.log('🎉 Proceso iniciado - Esperando pago');
    setReservando(false);

  } catch (err) {
    console.error('❌ Error al crear reserva:', err);
    alert('Error al crear la reserva: ' + (err.response?.data?.message || err.message));
    setReservando(false);
    setEsperandoPago(false);
  }
};

  const enviarWhatsApp = () => {
    if (!citaConfirmada || !citaConfirmada.telefonoChica) {
      alert('No se pudo obtener el teléfono de contacto');
      return;
    }

    const mensaje = encodeURIComponent(citaConfirmada.mensajeWhatsApp);
    const url = `https://wa.me/51${citaConfirmada.telefonoChica}?text=${mensaje}`;
    window.open(url, '_blank');
  };

  const calcularHoraFin = () => {
    if (!horaInicio) return '';
    const [hora, minuto] = horaInicio.split(':').map(Number);
    const horaFin = hora + horas;
    return `${horaFin.toString().padStart(2, '0')}:${minuto.toString().padStart(2, '0')}`;
  };

  const calcularTotal = () => {
    if (!chica || !chica.tarifaHora) return 0;
    return chica.tarifaHora * horas;
  };

  const obtenerNombreDia = (dayOfWeek) => {
    const dias = {
      'MONDAY': 'Lunes',
      'TUESDAY': 'Martes',
      'WEDNESDAY': 'Miércoles',
      'THURSDAY': 'Jueves',
      'FRIDAY': 'Viernes',
      'SATURDAY': 'Sábado',
      'SUNDAY': 'Domingo'
    };
    return dias[dayOfWeek] || dayOfWeek;
  };

  // ============================================
  // VISTA DE PAGO EXITOSO
  // ============================================
  if (mostrarConfirmacion && citaConfirmada) {
    return (
      <div className="pago-exitoso-container" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #FFF5F8 0%, #FFE8F0 100%)',
        padding: '20px'
      }}>
        <div className="pago-exitoso-card" style={{
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          maxWidth: '600px',
          width: '100%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div className="success-icon" style={{fontSize: '80px', marginBottom: '20px'}}>✅</div>
          <h1 style={{fontSize: '2rem', color: '#333', marginBottom: '10px'}}>¡Pago Confirmado!</h1>
          <p style={{color: '#666', marginBottom: '30px'}}>Tu cita ha sido confirmada exitosamente</p>

          <div className="cita-detalles" style={{
            background: '#f9f9f9',
            padding: '20px',
            borderRadius: '12px',
            margin: '20px 0',
            textAlign: 'left'
          }}>
            <div className="detalle-item" style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '12px 0',
              borderBottom: '1px solid #eee'
            }}>
              <strong>Código de Cita:</strong>
              <span style={{
                fontFamily: 'monospace',
                background: '#e91e63',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '6px',
                fontSize: '0.9rem'
              }}>{citaConfirmada.codigoCita}</span>
            </div>
            <div className="detalle-item" style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '12px 0',
              borderBottom: '1px solid #eee'
            }}>
              <strong>Fecha:</strong>
              <span>{citaConfirmada.fecha}</span>
            </div>
            <div className="detalle-item" style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '12px 0',
              borderBottom: '1px solid #eee'
            }}>
              <strong>Hora:</strong>
              <span>{citaConfirmada.horaInicio} - {citaConfirmada.horaFin}</span>
            </div>
            <div className="detalle-item" style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '12px 0',
              borderBottom: '1px solid #eee'
            }}>
              <strong>Con:</strong>
              <span>{citaConfirmada.nombreChica}</span>
            </div>
            <div className="detalle-item" style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '12px 0',
              background: '#fff3f8',
              margin: '10px -20px 0',
              paddingLeft: '20px',
              paddingRight: '20px',
              borderRadius: '8px'
            }}>
              <strong>Total Pagado:</strong>
              <span style={{fontSize: '1.2rem', fontWeight: 'bold', color: '#e91e63'}}>
                S/ {citaConfirmada.total}
              </span>
            </div>
          </div>

          <div className="contacto-section" style={{
            background: 'linear-gradient(135deg, #e91e63, #c2185b)',
            color: 'white',
            padding: '25px',
            borderRadius: '12px',
            margin: '25px 0'
          }}>
            <h3 style={{marginBottom: '15px'}}>📞 Contacto Desbloqueado</h3>
            <p style={{
              fontSize: '1.8rem',
              fontWeight: 'bold',
              margin: '15px 0',
              letterSpacing: '2px'
            }}>{citaConfirmada.telefonoChica}</p>
            <button 
              onClick={enviarWhatsApp}
              style={{
                background: '#25D366',
                color: 'white',
                padding: '14px 30px',
                border: 'none',
                borderRadius: '30px',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: '1rem',
                transition: 'all 0.3s'
              }}
            >
              💬 Contactar por WhatsApp
            </button>
          </div>

          <div className="instrucciones" style={{
            textAlign: 'left',
            margin: '25px 0',
            background: '#fff9fb',
            padding: '20px',
            borderRadius: '12px'
          }}>
            <h4 style={{color: '#e91e63', marginBottom: '15px'}}>Próximos Pasos:</h4>
            <ol style={{paddingLeft: '20px', lineHeight: '2'}}>
              <li>Contacta a {citaConfirmada.nombreChica} por WhatsApp</li>
              <li>Confirma los detalles de la cita</li>
              <li>Guarda tu código de cita: <strong>{citaConfirmada.codigoCita}</strong></li>
            </ol>
          </div>

          <button 
            onClick={() => navigate('/')}
            style={{
              padding: '14px 40px',
              background: '#e91e63',
              color: 'white',
              border: 'none',
              borderRadius: '30px',
              fontWeight: 'bold',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.3s',
              marginTop: '20px'
            }}
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // ✅ VISTA DE ESPERANDO PAGO CON TEMPORIZADOR
  // ============================================
  if (esperandoPago) {
    const minutos = Math.floor(tiempoRestante / 60);
    const segundos = tiempoRestante % 60;
    const porcentajeRestante = (tiempoRestante / 300) * 100;
    
    const getColor = () => {
      if (tiempoRestante > 180) return '#4CAF50';
      if (tiempoRestante > 60) return '#ff9800';
      return '#f44336';
    };

    // ✅ PANTALLA DE EXPIRACIÓN
    if (citaExpirada) {
      return (
        <div className="cita-expirada-container" style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #FFF5F8 0%, #FFE8F0 100%)',
          padding: '20px'
        }}>
          <div className="expirada-card" style={{
            background: 'white',
            borderRadius: '20px',
            padding: '40px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            textAlign: 'center'
          }}>
            <div className="icon" style={{fontSize: '80px', marginBottom: '20px'}}>⏰</div>
            <h1 style={{fontSize: '2rem', color: '#f44336', marginBottom: '15px'}}>
              Tiempo de Pago Expirado
            </h1>
            <p style={{color: '#666', lineHeight: '1.8', marginBottom: '20px'}}>
              El tiempo límite de 5 minutos para completar el pago ha expirado.
              <br/>
              La cita ha sido cancelada automáticamente.
            </p>
            
            <div style={{
              background: '#fff3f8',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '30px'
            }}>
              <p style={{color: '#e91e63', fontWeight: '600', marginBottom: '10px'}}>
                ¿Qué significa esto?
              </p>
              <ul style={{
                textAlign: 'left',
                color: '#666',
                lineHeight: '2',
                paddingLeft: '20px'
              }}>
                <li>La cita fue cancelada para liberar el horario</li>
                <li>Otras personas pueden reservar ese mismo horario ahora</li>
                <li>No se realizó ningún cargo a tu tarjeta</li>
              </ul>
            </div>

            <div style={{display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap'}}>
              <button
                onClick={() => {
                  setEsperandoPago(false);
                  setCitaExpirada(false);
                  setCitaIdEsperando(null);
                  window.location.reload();
                }}
                style={{
                  padding: '14px 30px',
                  background: '#e91e63',
                  color: 'white',
                  border: 'none',
                  borderRadius: '30px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  transition: 'all 0.3s'
                }}
              >
                🔄 Intentar Nuevamente
              </button>
              <button
                onClick={() => navigate('/')}
                style={{
                  padding: '14px 30px',
                  background: '#f5f5f5',
                  color: '#333',
                  border: 'none',
                  borderRadius: '30px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  transition: 'all 0.3s'
                }}
              >
                Volver al Inicio
              </button>
            </div>
          </div>
        </div>
      );
    }

    // ✅ PANTALLA DE ESPERA CON TEMPORIZADOR
    return (
      <div className="esperando-pago-container" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #FFF5F8 0%, #FFE8F0 100%)',
        padding: '20px'
      }}>
        <div className="esperando-card" style={{
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div className="spinner" style={{
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #e91e63',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          
          <h2 style={{color: '#333', marginBottom: '15px'}}>
            Esperando confirmación del pago...
          </h2>
          
          <p style={{color: '#666', lineHeight: '1.8', marginBottom: '30px'}}>
            Por favor completa el pago en la ventana de Mercado Pago.
            <br/>
            Esta página se actualizará automáticamente.
          </p>

          {/* ✅ TEMPORIZADOR VISUAL */}
          <div style={{
            background: '#f9f9f9',
            padding: '25px',
            borderRadius: '12px',
            marginBottom: '20px'
          }}>
            <p style={{
              fontSize: '0.9rem',
              color: '#666',
              marginBottom: '15px',
              fontWeight: '600'
            }}>
              ⏱️ Tiempo restante para completar el pago:
            </p>
            
            <div style={{
              fontSize: '3rem',
              fontWeight: 'bold',
              color: getColor(),
              marginBottom: '15px',
              fontFamily: 'monospace'
            }}>
              {String(minutos).padStart(2, '0')}:{String(segundos).padStart(2, '0')}
            </div>

            {/* Barra de progreso */}
            <div style={{
              width: '100%',
              height: '8px',
              background: '#e0e0e0',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${porcentajeRestante}%`,
                height: '100%',
                background: getColor(),
                transition: 'all 0.3s ease'
              }}></div>
            </div>

            {tiempoRestante <= 60 && (
              <p style={{
                marginTop: '15px',
                color: '#f44336',
                fontWeight: '600',
                fontSize: '0.9rem',
                animation: 'pulse 1s infinite'
              }}>
                ⚠️ ¡Menos de 1 minuto restante!
              </p>
            )}
          </div>

          <p style={{
            padding: '15px',
            background: '#fff9fb',
            borderRadius: '8px',
            fontSize: '0.85rem',
            color: '#666',
            lineHeight: '1.6'
          }}>
            💡 Si cierras la ventana de pago, tienes hasta que expire el tiempo para volver a intentarlo.
            <br/>
            Después de 5 minutos, la cita se cancelará automáticamente.
          </p>
        </div>
        
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  // ============================================
  // VISTA NORMAL DEL PERFIL
  // ============================================
  if (loading) {
    return (
      <div className="loading" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '24px'
      }}>
        <h2>Cargando...</h2>
      </div>
    );
  }

  if (error || !chica) {
    return (
      <div className="error" style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: '20px'
      }}>
        <h2>{error || 'Chica no encontrada'}</h2>
        <button onClick={() => navigate('/')} style={{
          padding: '12px 24px',
          background: '#ec4899',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer'
        }}>
          Volver al inicio
        </button>
      </div>
    );
  }

  const imagenes = chica.imagenes || [chica.imagen];

  return (
    <div className="perfil-page">
      {/* Header */}
      <header className="perfil-header">
        <div className="container">
          <div className="perfil-header-content">
            <h1 className="perfil-logo" onClick={() => navigate('/')} style={{cursor: 'pointer'}}>
              <span className="logo-text">Novia de Alquiler</span>
              <span className="logo-brand">"Petit Kano"</span>
            </h1>
            <div className="perfil-locations">
              {chica.distritosDisponibles ? chica.distritosDisponibles.join(' / ') : 'LIMA'}
            </div>
          </div>
          <nav className="perfil-nav">
            <button onClick={() => navigate('/')}>Página Principal</button>
            <button onClick={() => window.scrollTo({top: document.getElementById('disponibilidad')?.offsetTop || 0, behavior: 'smooth'})}>Disponibilidad</button>
            <button onClick={() => window.scrollTo({top: document.getElementById('reserva')?.offsetTop || 0, behavior: 'smooth'})}>Reservar</button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <div className="perfil-container">
        {/* Sección Superior */}
        <div className="perfil-main">
          <div className="perfil-nombre-seccion">
            <h2 className="perfil-nombre-jp">{chica.nombre}</h2>
            <div className="perfil-tags-principales">
              {chica.tags && chica.tags.map((tag, idx) => (
                <span key={idx} className={`perfil-tag ${tag.includes('Ranking') ? 'ranking-tag' : ''}`}>
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Galería */}
          <div className="perfil-galeria">
            <div className="perfil-imagen-principal">
              <img src={imagenes[imagenActual]} alt={chica.nombre} />
              {imagenes.length > 1 && (
                <>
                  <button className="galeria-btn prev" onClick={() => setImagenActual((imagenActual - 1 + imagenes.length) % imagenes.length)}>‹</button>
                  <button className="galeria-btn next" onClick={() => setImagenActual((imagenActual + 1) % imagenes.length)}>›</button>
                </>
              )}
            </div>
            {imagenes.length > 1 && (
              <div className="perfil-miniaturas">
                {imagenes.map((img, idx) => (
                  <img 
                    key={idx} 
                    src={img} 
                    alt={`${chica.nombre} ${idx + 1}`}
                    className={imagenActual === idx ? 'active' : ''}
                    onClick={() => setImagenActual(idx)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Información Básica */}
          <div className="perfil-info-table">
            <h3>📋 Información</h3>
            
            <div className="info-row">
              <span className="info-label">Edad</span>
              <span className="info-value">{chica.edad} años</span>
            </div>

            <div className="info-row">
              <span className="info-label">Distritos disponibles</span>
              <span className="info-value">
                {chica.distritosDisponibles ? chica.distritosDisponibles.join(', ') : chica.distrito}
              </span>
            </div>

            <div className="info-row">
              <span className="info-label">Tarifa por hora</span>
              <span className="info-value">S/ {chica.tarifaHora}</span>
            </div>

            <div className="info-row">
              <span className="info-label">Horas mínimas</span>
              <span className="info-value">{chica.horasMinimas} horas</span>
            </div>

            <div className="info-row">
              <span className="info-label">Horas máximas</span>
              <span className="info-value">{chica.horasMaximas} horas</span>
            </div>

            <div className="info-row">
              <span className="info-label">Descripción</span>
              <span className="info-value">{chica.descripcion}</span>
            </div>

            <div className="perfil-tipos-tags" style={{marginTop: '20px'}}>
              {chica.tipos && chica.tipos.map((tipo, idx) => (
                <span key={idx} className="tipo-tag">
                  {tipos.find(t => t.id === tipo)?.nombre || tipo}
                </span>
              ))}
            </div>
          </div>

          {/* Calendario de Disponibilidad */}
          <div className="disponibilidad-section" id="disponibilidad">
            <h3>📅 Disponibilidad Semanal</h3>
            {disponibilidades.length === 0 ? (
              <p style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                No hay disponibilidad configurada
              </p>
            ) : (
              <div className="disponibilidad-lista">
                {disponibilidades.map((disp, idx) => (
                  <div key={idx} className="disponibilidad-item">
                    <div className="disponibilidad-dia">
                      {obtenerNombreDia(disp.diaSemana)}
                    </div>
                    <div className="disponibilidad-horario">
                      {disp.horaInicio} - {disp.horaFin}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Formulario de Reserva */}
        <div className="perfil-sidebar">
          <div className="simulador-card" id="reserva">
            <h3 className="simulador-titulo">💝 Reservar Cita</h3>
            
            <div className="simulador-control">
              <label>Fecha</label>
              <input 
                type="date"
                value={fechaSeleccionada}
                onChange={(e) => setFechaSeleccionada(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="form-input"
                style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}}
              />
            </div>

            <div className="simulador-control">
              <label>Hora de inicio</label>
              <div style={{position: 'relative'}}>
                <input 
                  type="text"
                  value={horaInicio}
                  readOnly
                  onClick={() => setMostrarSelectorHora(!mostrarSelectorHora)}
                  className="form-input"
                  style={{
                    width: '100%', 
                    padding: '8px', 
                    borderRadius: '4px', 
                    border: '1px solid #ddd',
                    cursor: 'pointer',
                    backgroundColor: 'white'
                  }}
                />
                {mostrarSelectorHora && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    marginTop: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    maxHeight: '250px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '0',
                      maxHeight: '250px',
                      overflow: 'auto'
                    }}>
                      {/* Columna de horas */}
                      <div style={{borderRight: '1px solid #eee', overflowY: 'auto', maxHeight: '250px'}}>
                        {Array.from({length: 24}, (_, i) => i).map(hora => (
                          <div
                            key={`h-${hora}`}
                            onClick={() => {
                              const minutoActual = horaInicio.split(':')[1] || '00';
                              setHoraInicio(`${String(hora).padStart(2, '0')}:${minutoActual}`);
                            }}
                            style={{
                              padding: '10px 15px',
                              cursor: 'pointer',
                              backgroundColor: horaInicio.split(':')[0] === String(hora).padStart(2, '0') ? '#ec4899' : 'white',
                              color: horaInicio.split(':')[0] === String(hora).padStart(2, '0') ? 'white' : '#333',
                              textAlign: 'center',
                              fontWeight: horaInicio.split(':')[0] === String(hora).padStart(2, '0') ? 'bold' : 'normal',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (horaInicio.split(':')[0] !== String(hora).padStart(2, '0')) {
                                e.target.style.backgroundColor = '#f8f8f8';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (horaInicio.split(':')[0] !== String(hora).padStart(2, '0')) {
                                e.target.style.backgroundColor = 'white';
                              }
                            }}
                          >
                            {String(hora).padStart(2, '0')}
                          </div>
                        ))}
                      </div>
                      
                      {/* Columna de minutos */}
                      <div style={{overflowY: 'auto', maxHeight: '250px'}}>
                        {Array.from({length: 60}, (_, i) => String(i).padStart(2, '0')).map(minuto => (
                          <div
                            key={`m-${minuto}`}
                            onClick={() => {
                              const horaActual = horaInicio.split(':')[0] || '00';
                              setHoraInicio(`${horaActual}:${minuto}`);
                              setMostrarSelectorHora(false);
                            }}
                            style={{
                              padding: '10px 15px',
                              cursor: 'pointer',
                              backgroundColor: horaInicio.split(':')[1] === minuto ? '#ec4899' : 'white',
                              color: horaInicio.split(':')[1] === minuto ? 'white' : '#333',
                              textAlign: 'center',
                              fontWeight: horaInicio.split(':')[1] === minuto ? 'bold' : 'normal',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              if (horaInicio.split(':')[1] !== minuto) {
                                e.target.style.backgroundColor = '#f8f8f8';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (horaInicio.split(':')[1] !== minuto) {
                                e.target.style.backgroundColor = 'white';
                              }
                            }}
                          >
                            {minuto}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="simulador-control">
              <label>Duración (horas)</label>
              <div className="tiempo-selector">
                <button onClick={() => setHoras(Math.max(chica.horasMinimas, horas - 1))}>-</button>
                <span>{horas} hora{horas > 1 ? 's' : ''}</span>
                <button onClick={() => setHoras(Math.min(chica.horasMaximas, horas + 1))}>+</button>
              </div>
            </div>

            <div className="simulador-control">
              <label>Distrito</label>
              <select 
                value={distritoSeleccionado}
                onChange={(e) => setDistritoSeleccionado(e.target.value)}
                className="form-input"
                style={{width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd'}}
              >
                {chica.distritosDisponibles && chica.distritosDisponibles.map((distrito, idx) => (
                  <option key={idx} value={distrito}>{distrito}</option>
                ))}
              </select>
            </div>

            <div className="calculo-detalles">
              <div className="calculo-row">
                <span>Tarifa: S/ {chica.tarifaHora} × {horas} horas</span>
                <span>= S/ {calcularTotal()}</span>
              </div>
              <div className="calculo-row">
                <span>Hora fin:</span>
                <span>{calcularHoraFin()}</span>
              </div>
            </div>

            <div className="resultado-calculo">
              <span>Total a pagar</span>
              <span className="precio-total">S/ {calcularTotal()}</span>
            </div>

            <button 
              onClick={handleReservar}
              disabled={reservando}
              className="btn-reservar"
              style={{
                width: '100%',
                padding: '16px',
                background: reservando ? '#ccc' : '#ec4899',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: reservando ? 'not-allowed' : 'pointer',
                marginTop: '20px'
              }}
            >
              {reservando ? 'Procesando...' : '💳 Pagar con Mercado Pago'}
            </button>

            <div className="nota-pago" style={{
              fontSize: '12px',
              marginTop: '12px',
              padding: '12px',
              background: '#fff9fb',
              borderRadius: '8px',
              lineHeight: '1.6'
            }}>
              <p style={{margin: '5px 0'}}>💳 <strong>Pago seguro con Mercado Pago</strong></p>
              <p style={{margin: '5px 0'}}>✅ Se abrirá en nueva pestaña</p>
              <p style={{margin: '5px 0'}}>⏱️ Tienes 5 minutos para completar el pago</p>
              <p style={{margin: '5px 0'}}>🔒 Transacción 100% segura</p>
              <p style={{margin: '5px 0', color: '#666'}}>
                * Después del pago recibirás el código de cita y el teléfono de contacto
              </p>
            </div>
          </div>

          {/* Botón alternativo de WhatsApp */}
          <div className="botones-accion" style={{marginTop: '20px'}}>
            <a 
              href={`https://wa.me/51951329353?text=Hola, me gustaría reservar una cita con ${chica.nombre}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-aplicar line"
              style={{
                display: 'block',
                textAlign: 'center',
                padding: '12px',
                background: '#25D366',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: 'bold'
              }}
            >
              💬 Contactar con el admin
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="perfil-footer">
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="volver-arriba">
          Volver arriba ↑
        </button>
      </footer>
    </div>
  );
}

export default PerfilChica;















